/**
 * RECORD-360-WORKSPACE-V1 (V1-P5) -- Scope 2: the always-visible RECORD
 * HEADER + CURRENT-STATE BAND for the canonical Lead-360 workspace.
 *
 * Every value renders from THE current-truth contract
 * (frontend/src/dashboard/record/current-truth.ts) -- this file computes
 * nothing itself, so the header, the band, and the quick-view drawer can
 * never disagree (they consume the same resolver over the same read).
 *
 * STAGE DISPLAY RULE (Scope 2c): the operator-owned CRM stage renders
 * LABELED as such and never headlines over operational truth -- the band
 * leads with the operational chips (appointment / conversion / payment /
 * patient link / follow-up); the stage chip sits after them with an
 * explicit "CRM stage" micro-label. No stage derivation, no stage writes.
 *
 * 30-SECOND SCAN CONTRACT (acceptance 1): who (name/ref + UMR), stage,
 * latest activity, current appointment, converted?, money pending?, next
 * action, owner -- all answerable from this header + band without
 * scrolling. Chips and short fields only; zero paragraphs.
 */

import type { ReactNode } from 'react';

import type { Lead360Input } from '@/dashboard/owner/lead-360-view';
import {
  formatTimestampIst,
  leadRefInitials,
  resolveOriginChipLabel,
  sourceChannelLabel,
} from '@/dashboard/owner/Lead360Panel';
import { buildLead360View } from '@/dashboard/owner/lead-360-view';
import { PHONE_AVAILABILITY_COPY } from '@/dashboard/owner/LeadContactReveal';
import { latestActivitySummary } from '@/dashboard/owner/leads-object-view';
import {
  buildLeadSummaryTiles,
  resolveOwnershipPlaneRow,
  type LeadSummaryTile,
  type UpcomingItem,
} from '@/dashboard/leads/lead-record-experience';
import {
  AcBadge,
  KpiTile,
  FieldGrid,
  QuietAction,
  type AcBadgeTone,
  type FieldGridField,
  type KpiTileTone,
} from '@/dashboard/ui';
import type {
  ConversionKind,
  LeadRecordCurrentTruth,
  PaymentStateKind,
} from '@/dashboard/record/current-truth';

export const NO_APPOINTMENT_CHIP_LABEL = 'No appointment';
export const FOLLOW_UP_NONE_LABEL = 'No follow-up due';
export const STAGE_MICRO_LABEL = 'CRM stage';

/**
 * HONEST CHIP SCOPES (LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1, Fix 3 --
 * the ONE-NUMBER law applied to chips; Phase-1 finding: two 'outcome' planes
 * collided on one page).
 *   - The conversion chip is the APPOINTMENT/VISIT plane (appointment_events
 *     polar tokens + finalized-visit plan counts) -- labeled 'Visit outcome'
 *     so it never reads as the CALL plane the disposition panel records (the
 *     call plane's home is the Last-contact block + 'Last contact result').
 *   - The follow-up chip derives PURELY from the treatment/conversion plane
 *     (advised decisions, partial acceptance, recoverable non-conversion) --
 *     labeled 'Treatment follow-up' so 'No follow-up due' never contradicts
 *     a pending agent callback (whose homes are the Upcoming list, the
 *     overdue alert strip, and the 'Callback due' field).
 */
export const CONVERSION_CHIP_LABEL = 'Visit outcome';
export const FOLLOW_UP_CHIP_LABEL = 'Treatment follow-up';

const CONVERSION_TONE: Readonly<Record<ConversionKind, AcBadgeTone>> = {
  none: 'neutral',
  awaiting_decision: 'info',
  converted: 'positive',
  partially_accepted: 'info',
  not_converted: 'danger',
  contradiction: 'danger',
};

const PAYMENT_TONE: Readonly<Record<PaymentStateKind, AcBadgeTone>> = {
  recorded: 'positive',
  collect_pending: 'pending',
  advised_only_no_handoff: 'info',
  none: 'neutral',
  unknown_on_this_read: 'neutral',
};

/** R-29 SLA chip tone by breach severity (neutral covers clear/unavailable). */
const SLA_TONE: Readonly<Record<'neutral' | 'amber' | 'red', AcBadgeTone>> = {
  neutral: 'neutral',
  amber: 'pending',
  red: 'danger',
};

/** The summary strip's tone vocabulary maps 1:1 onto the tile canon. */
const SUMMARY_TILE_TONE: Readonly<Record<LeadSummaryTile['tone'], KpiTileTone>> = {
  ok: 'ok',
  warm: 'warm',
  breach: 'breach',
};

/**
 * LEAD-RECORD-POLISH-BATCH-V1 (CC-04) -- the shared SummaryHeader strip,
 * rendered as the record's own tile row. It composes the FROZEN canon
 * (KpiTile, the shell's `.ac-summary__tiles` rhythm) and invents no visual
 * vocabulary; the tiles' facts come from ONE pure builder
 * (buildLeadSummaryTiles) so the header can never disagree with the band.
 *
 * Legacy test ids ride the tile VALUES (record360-owner /
 * record360-next-action-owner): the facts MOVED to their one home, they were
 * not renamed and not duplicated.
 */
function LeadSummaryStrip({
  tiles,
}: {
  tiles: readonly LeadSummaryTile[];
}): JSX.Element | null {
  if (tiles.length === 0) return null;
  return (
    <div
      className="ac-summary__tiles record360-header__summary"
      role="group"
      aria-label="Lead summary"
      data-testid="lead-summary-tiles"
    >
      {tiles.map((tile) => (
        <KpiTile
          key={tile.key}
          label={tile.label}
          tone={SUMMARY_TILE_TONE[tile.tone]}
          testId={tile.testId}
          value={
            <span
              data-testid={tile.valueTestId}
              data-plane={tile.plane}
              className="record360-header__summary-value"
            >
              {tile.value}
            </span>
          }
          sub={tile.sub}
        />
      ))}
    </div>
  );
}

/**
 * LEAD360-B2-G5-POLISH-AND-CLOSURE M5: the appointment-STATE badge, ONE
 * render extracted so both the title-row slot (non-compactMeta hosts) and
 * the composed rail's Appointments section `stat` (compactMeta hosts, i.e.
 * Lead 360) share the exact SAME value/tone logic and the exact SAME test
 * id (`record360-chip-appointment`) -- never a second derivation, never a
 * second contract to keep in sync.
 */
export function AppointmentStatusChip({
  truth,
}: {
  truth: LeadRecordCurrentTruth;
}): JSX.Element {
  return (
    <AcBadge
      tone={truth.currentAppointment.kind === 'none' ? 'neutral' : 'brand'}
      testId="record360-chip-appointment"
    >
      {truth.currentAppointment.statusLabel ?? NO_APPOINTMENT_CHIP_LABEL}
    </AcBadge>
  );
}

/**
 * The always-visible record header: identity (or the honest ref), lead id +
 * UMR when linked, clinic, operational status headline, source, last
 * meaningful activity -- plus the caller's primary-actions slot.
 *
 * LEAD-RECORD-POLISH-BATCH-V1 (THE RECOMPOSITION LAW -- FIRST SCREEN = THE
 * WHOLE STORY): the header now opens with the CC-04 summary strip, so who /
 * where from / how long waiting / what state / who owns it all resolve at the
 * top of the first screen. The identity facts stay a labeled FieldGrid
 * underneath (FIELDS, NOT SENTENCES) but SHED the two facts the strip now
 * owns -- Owner and the routing plane -- so no fact renders twice (DENSITY
 * rule 1). Zero information loss: every value still renders, once, in the
 * home that answers fastest.
 */
export function RecordHeader({
  record,
  truth,
  actions,
  assignmentMode,
  nowMs,
  showSummaryStrip = true,
  compactMeta = false,
  revealControl,
  revealInvoked = false,
  onInvokeReveal,
}: {
  record: Lead360Input;
  truth: LeadRecordCurrentTruth;
  /** LAWFUL-ACTIONS ONLY (Scope 2b): callers pass role-held doors only. */
  actions?: ReactNode;
  /**
   * `me.leadAssignmentMode` (server truth) -- feeds the CC-11 unclaimed
   * explanation. OMITTED (tests / SSR / a caller outside the assignment
   * audience) => the seat states the queue and claims NO reason.
   */
  assignmentMode?: string | null;
  /**
   * Injected clock for the waiting age (the module stays deterministic).
   * OMITTED => the strip renders the honest "-" waiting value.
   */
  nowMs?: number;
  /**
   * LEAD-360-CONFORMANCE-B2 (RECORD360-LEAD-COMPOSED-V2 masthead grammar --
   * F2 region 3: "meta line ... actions = overflow ONLY", no tile strip).
   * DEFAULT TRUE => byte-identical for every other host. The canonical Lead
   * 360 page passes FALSE: the strip's four facts (Channel / Waiting / Team
   * / Claimed by) relocate to the composed rail's Owner section (Waiting +
   * the ownership-plane note) and the Campaign-and-source section (Channel);
   * nothing is deleted, only relocated (SAY-ONCE RIDER, Ruling 1 -- see the
   * B2 run record).
   */
  showSummaryStrip?: boolean;
  /**
   * LEAD-360-CONFORMANCE-B2 (F2 region 3 "meta line (Lead #, clinic,
   * captured, 'Phone on file') ... one-line meta"): collapses the identity
   * FieldGrid into the pack's terse single-line grammar and promotes
   * Patient ID/UMR to a masthead CHIP (the CONVERTED frame's "Patient ID
   * ... chip beside the name"). DEFAULT FALSE => every other host (Patient
   * Record, Doctor patient lens, the owner kill-switch render) keeps the
   * existing FieldGrid byte-identically. "Latest activity" is not one of
   * the pack's four meta-line facts, but survives as its OWN small line
   * directly beneath it, same test id, same value -- never dropped, since
   * LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1's re-derivation proof
   * reads this exact fact after a mutation (a scoped, named deviation from
   * the pack's literal 4-fact list, not a silent addition).
   */
  compactMeta?: boolean;
  /**
   * LEAD-360-CONFORMANCE-B2 G1 (the reveal control relocates FROM the doomed
   * Contact card TO the masthead meta line -- "reveal-phone machinery and
   * all writer wiring survive relocation byte-identical - deletions are of
   * CONTAINERS, never of behavior"). The caller owns the control's element
   * and its invoked/audited state; this header only provides the
   * always-mounted anchor point (the NextActionEdge composer idiom) and the
   * one-line "Reveal" invocation affordance. OMITTED => no reveal
   * affordance renders here (every other host keeps its own reveal home
   * byte-identical).
   */
  revealControl?: ReactNode;
  /** Whether `revealControl` is currently shown (native `hidden`, never a
   *  conditional mount -- the always-mount idiom this file already uses for
   *  the composer). OMITTED => treated as false (control stays hidden). */
  revealInvoked?: boolean;
  /** Invoked by the meta line's "Reveal" affordance. OMITTED (revealControl
   *  also omitted) => no affordance renders, so this is never called. */
  onInvokeReveal?: () => void;
}): JSX.Element {
  const view = buildLead360View(record);
  // CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (finding 4): the
  // ownership-plane display rule -- see lead-record-experience.ts.
  const ownershipPlaneRow = resolveOwnershipPlaneRow(record, view);
  const summaryTiles = buildLeadSummaryTiles({
    record,
    view,
    truth,
    ownershipPlaneRow,
    assignmentMode,
    nowMs: nowMs ?? Number.NaN,
  });
  return (
    <header className="record360-header" data-testid="record360-header">
      <span
        className="lead360-avatar"
        aria-hidden="true"
        data-testid="lead360-avatar"
      >
        {leadRefInitials(view.safeLeadId)}
      </span>
      <div className="record360-header__identity">
        {/* LEAD360-B2-G5-POLISH-AND-CLOSURE M5: the "LEAD RECORD" eyebrow
            is REMOVED for the compact masthead (chronicle-normal.png's
            masthead carries no eyebrow line above the name) -- scoped to
            compactMeta so every other host (Patient Record, Doctor
            patient lens, the owner kill-switch render) keeps it
            byte-identically. */}
        {!compactMeta ? (
          <span className="lead360-record__eyebrow">Lead record</span>
        ) : null}
        <div className="record360-header__titlerow">
          <h1 className="record360-header__title" data-testid="record360-title">
            {view.leadName ?? `Lead #${view.safeLeadId}`}
          </h1>
          {/* LEAD-360-CONFORMANCE-B1: the pack's masthead chip order --
              name -> stage chip (neutral) -> origin-channel chip (F1 TIER 3
              "STAGE CHIP ORDER"). Both read from data this header already
              resolves (truth.currentLeadStage / the record's own source
              fields) -- no new derivation. */}
          <span
            className="ac-badge ac-badge--neutral"
            data-testid="record360-stage-chip"
          >
            {truth.currentLeadStage.label}
          </span>
          {/* LEAD-360-CONFORMANCE-B2 G3 (founder ruling, product-wide --
              docs/design/design-language/07-BEHAVIOURAL-LAWS.md ORIGIN-CHIP
              SEMANTICS): CAMPAIGN -> SOURCE -> CHANNEL is the LAW's layer
              order. LEAD360-B2-G5-POLISH-AND-CLOSURE M8 (the law's surface-
              binding rider, same doc): chronicle-details-1440x900.png
              proves the SAME fixture (Lead #214, campaign "smile-aug-2026"
              present) renders "WhatsApp" in the masthead chip while the
              rail's Campaign and source section shows the full Channel ->
              Medium -> Campaign chain -- the RECORD-PAGE archetype
              (compactMeta) binds its masthead chip to CHANNEL ONLY, never
              the campaign/source layers (those stay in the rail, always-
              present, never re-derived here). Every other (non-compactMeta)
              host keeps resolveOriginChipLabel's CAMPAIGN -> SOURCE ->
              CHANNEL ladder byte-identically -- only THIS surface binds
              differently; the law itself is unchanged product-wide. The
              CONVERTED frame shows Patient ID in the chip's place -- the
              origin chip is SUPPRESSED (not stacked) once a Patient ID chip
              renders. */}
          {compactMeta && truth.identityState.umr !== null
            ? null
            : (() => {
                const origin = compactMeta
                  ? sourceChannelLabel(record.sourceNormalized, record.utmSource)
                  : resolveOriginChipLabel({
                      sourceNormalized: record.sourceNormalized,
                      utmSource: record.utmSource,
                      utmCampaign: record.utmCampaign,
                    });
                return origin !== '' ? (
                  <span
                    className="ac-badge ac-badge--neutral"
                    data-testid="record360-origin-chip"
                  >
                    {origin}
                  </span>
                ) : null;
              })()}
          {/* LEAD-360-CONFORMANCE-B2 (F2 CONVERTED frame: "Patient ID
              AC-000123 chip beside the name"): Patient ID/UMR promotes from
              the identity FieldGrid to a masthead chip when compactMeta is
              on -- relocated, not duplicated (the FieldGrid's own Patient ID
              row is omitted in the SAME branch below). */}
          {compactMeta && truth.identityState.umr !== null ? (
            <span
              className="ac-badge ac-badge--positive"
              data-testid="record360-umr"
            >
              Patient ID {truth.identityState.umr}
            </span>
          ) : null}
          {/* THE APPOINTMENT-STATE RENDER (one render, one source).
              LEAD-RECORD-POLISH-BATCH-V1 E2E CORRECTIVE: the recomposition
              MOVED this fact off the band and into the title row, and the
              band chip's test id (`record360-chip-appointment`) was the
              e2e truth contract for EVERY appointment state ("No
              appointment" / "Booked" / the 409 double-book truth /
              Booked-beside-Lost). LEAD360-B2-G5-POLISH-AND-CLOSURE M5:
              chronicle-normal.png's masthead carries exactly TWO chips
              (status + channel) -- this THIRD chip is REMOVED from the
              compact masthead ("appointment state lives in the rail"); the
              SAME `AppointmentStatusChip` (below) now renders as the
              rail's Appointments section `stat` (always visible, open or
              closed -- SectionBand's own S4 contract), carrying the exact
              SAME test id, so the e2e contract survives the relocation.
              Every OTHER host (non-compactMeta) keeps this title-row render
              byte-identically -- `record360-status-headline` stays that
              slot's own pin there. */}
          {!compactMeta ? (
            <span
              className="record360-header__status"
              data-testid="record360-status-headline"
            >
              <AppointmentStatusChip truth={truth} />
            </span>
          ) : null}
          {/* LEAD-RECORD-POLISH-BATCH-V1 (DENSITY -- kill the whitespace): the
              actions ride the TITLE ROW instead of a third header column. The
              old three-column header gave the action cluster half the width
              and squeezed the identity facts into a narrow left gutter, which
              is exactly the "excessive whitespace / poor placement" the walk
              found. On the title row they right-align beside the name, and the
              identity grid + summary strip below get the FULL header width. */}
          {actions !== undefined && actions !== null ? (
            <div className="record360-header__actions" data-testid="record360-actions">
              {actions}
            </div>
          ) : null}
        </div>
        {compactMeta ? (
          <>
            {/* LEAD-360-CONFORMANCE-B2 (F2 region 3 "meta line (Lead #,
                clinic, captured, 'Phone on file') ... one-line meta"): the
                pack's terse inline grammar -- Patient ID moved to the title
                row's chip above (never duplicated); the raw attribution
                PARAMETERS keep their collapsed home in the rail's Campaign
                and source section. No reveal AFFORDANCE lives here (the
                phone-availability FACT is honest and passive) -- the
                existing audited reveal control stays the Contact section's
                alone, never a second writer. */}
            <p className="record360-header__meta" data-testid="record360-meta">
              <span data-testid="record360-ref">Lead #{view.safeLeadId}</span>
              {' · '}
              {view.clinicLabel}
              {' · '}
              Captured{' '}
              <span data-testid="record360-captured">
                {formatTimestampIst(view.createdAtDisplay)}
              </span>
              {' · '}
              {PHONE_AVAILABILITY_COPY[record.phoneAvailability ?? 'unknown']}
              {/* LEAD-360-CONFORMANCE-B2 G1: the reveal-phone AFFORDANCE
                  relocates here from the doomed Contact card; the control
                  itself (reveal-phone MACHINERY + writer wiring) is the
                  caller's own `revealControl`, mounted byte-identical below,
                  only its container moved. OMITTED (no revealControl passed)
                  => no affordance, matching every other compactMeta host. */}
              {revealControl !== undefined ? (
                <>
                  {' '}
                  <QuietAction
                    type="button"
                    size="sm"
                    data-testid="record360-meta-reveal-trigger"
                    aria-expanded={revealInvoked}
                    onClick={() => onInvokeReveal?.()}
                  >
                    Reveal
                  </QuietAction>
                </>
              ) : null}
            </p>
            {/* LEAD360-B2-G5-POLISH-AND-CLOSURE M1 (ONE-TRUTH defect,
                DELETED): this line derived from `latestActivitySummary`,
                which is NOT wire-ordered by recency -- it rendered the
                lead's OLDEST event ("Lead captured...") captioned "Latest
                activity" while the promoted timeline's true latest entry
                (its own `.row.latest` accent) was a DIFFERENT, newer touch
                -- a same-screen contradiction. chronicle-normal.png's
                masthead carries no such line. The timeline is now the ONE
                place "what's latest" is answered, correctly, from the
                real wire order via its own structural latest-accent (no
                separate derivation to keep in sync). The OTHER host
                (non-compactMeta FieldGrid, below) is UNCHANGED -- this
                deletion is scoped to Lead 360's own masthead only. */}
            {/* Always-mount, native `hidden` (the NextActionEdge composer
                idiom -- signatures.css pairs `[hidden]` with `display: none
                !important` so author CSS can never defeat it). The control
                mounts once here regardless of invocation so its own state
                (a reveal audit write in flight, an already-revealed value)
                never resets on toggle. */}
            {revealControl !== undefined ? (
              <div hidden={!revealInvoked} data-testid="record360-meta-reveal-slot">
                {revealControl}
              </div>
            ) : null}
          </>
        ) : (
          /* V7-P4A (rider R-30): the dash-spliced meta/sub prose becomes the
             canonical FieldGrid -- LABELED identity facts (FIELDS NOT
             SENTENCES), same values, same test ids, same role gating. The
             field set is deliberately CAPPED at the scan-critical facts
             (identity / clinic / source / captured / activity / owner /
             next-action-owner); everything else lives in the zones below. */
          <FieldGrid
            testId="record360-meta"
            label="Lead identity facts"
            fields={
              [
                {
                  label: 'Lead',
                  value: (
                    <span data-testid="record360-ref">
                      Lead #{view.safeLeadId}
                    </span>
                  ),
                },
                ...(truth.identityState.umr !== null
                  ? [
                      {
                        label: 'Patient ID',
                        value: (
                          <span data-testid="record360-umr">
                            {truth.identityState.umr}
                          </span>
                        ),
                      },
                    ]
                  : []),
                { label: 'Clinic', value: view.clinicLabel },
                /* CC-04 + DENSITY rule 1: the normalized-source fact moved UP
                   into the summary strip's Channel tile (its one always-visible
                   home, per the E5a normalized-source law -- 'Source' stays the
                   details-zone section title and never becomes a second
                   heading). The raw attribution PARAMETERS keep their collapsed
                   home in the Details zone. Nothing is lost; nothing repeats. */
                {
                  label: 'Captured',
                  value: (
                    <span data-testid="record360-captured">
                      {formatTimestampIst(view.createdAtDisplay)}
                    </span>
                  ),
                },
                {
                  label: 'Latest activity',
                  value: (
                    <span data-testid="record360-latest-activity">
                      {latestActivitySummary(view)}
                    </span>
                  ),
                },
                /* CC-08 + DENSITY rule 1 (SAY IT ONCE): the two ownership
                   planes -- the routing TEAM and the PERSON who claimed it --
                   moved to the summary strip above, which is now their ONE
                   home. Their test ids (record360-next-action-owner /
                   record360-owner) moved WITH them; the finding-4 rule
                   (a personally assigned lead renders NO ladder guess) and the
                   nextActionOwner role gate are unchanged, and are enforced in
                   the ONE builder (buildLeadSummaryTiles). */
              ] as FieldGridField[]
            }
          />
        )}
        {showSummaryStrip ? <LeadSummaryStrip tiles={summaryTiles} /> : null}
      </div>
    </header>
  );
}

function BandChip({
  label,
  tone,
  value,
  testId,
  emphasis = false,
}: {
  label: string;
  tone: AcBadgeTone;
  value: string;
  testId: string;
  /** V7-P4A (rider R-31): money/outcome chips carry the emphasis tier so
   *  the band stops reading as seven equal-weight chips. Presentation
   *  only -- order, values, tones, and test ids are unchanged. */
  emphasis?: boolean;
}): JSX.Element {
  return (
    <div
      className={
        emphasis
          ? 'record360-band__item record360-band__item--emphasis'
          : 'record360-band__item'
      }
      data-testid={testId}
    >
      <span className="record360-band__label">{label}</span>
      <AcBadge tone={tone}>{value}</AcBadge>
    </div>
  );
}

/**
 * The current-state band: appointment / conversion / payment / patient link
 * / follow-up first (operational truth leads), then the labeled CRM stage,
 * then the one next lawful action line. Historical reversals surface as a
 * labeled warnings strip -- never as current badges.
 */
/** The band chips a host may declare as ALREADY carried by its own summary. */
export type BandChipKey =
  | 'appointment'
  | 'conversion'
  | 'payment'
  | 'identity'
  | 'followup'
  | 'sla'
  | 'stage';

export function CurrentStateBand({
  truth,
  upcoming,
  omitChips,
  omitNextAction = false,
}: {
  truth: LeadRecordCurrentTruth;
  /**
   * ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1 (X1): the SAME upcoming/overdue
   * items the activity rail derives (buildUpcomingItems -- one derivation,
   * passed down, never recomputed differently). Overdue items surface as a
   * compact alert strip at the band's head so "is anything overdue" is
   * answerable above the fold on EVERY viewport (on mobile the rail sits
   * screens below). OMITTED => absent (byte-identical for other hosts).
   */
  upcoming?: readonly UpcomingItem[];
  /**
   * LEAD-RECORD-POLISH-BATCH-V1 (DENSITY rule 1 -- SAY IT ONCE): chips whose
   * fact the HOST already renders in its own first-screen summary. The host
   * DECLARES them so the fact renders exactly once on the page; the band
   * still owns every chip it is not told about.
   *
   * OMITTED (every other host, every direct render) => the full seven-chip
   * band, byte-identical. This never hides a fact: a caller may only omit a
   * chip whose value it renders itself, and zero information loss is the
   * page-level test.
   */
  omitChips?: readonly BandChipKey[];
  /**
   * LEAD-360-CONFORMANCE-B2: the host already renders the SAME
   * `truth.nextLawfulAction` sentence as the page's ONE loud line
   * (ui/NextActionEdge -- NEXT-LINE DOMINANCE). DEFAULT FALSE => byte-
   * identical for every other host (and every direct component-level
   * render). TRUE leaves the band as a pure residual strip: the overdue
   * alerts + historicalWarnings + the desk-closed visit note only --
   * every fact the deleted "old summary block" carried that the composed
   * pack's frames do not draw a home for stays reachable, never dropped.
   */
  omitNextAction?: boolean;
}): JSX.Element {
  const overdueItems = (upcoming ?? []).filter((u) => u.overdue);
  const omitted = new Set<BandChipKey>(omitChips ?? []);
  const shows = (key: BandChipKey): boolean => !omitted.has(key);
  // LEAD360-B2-G5-POLISH-AND-CLOSURE M5 (masthead compression, "no extra
  // spacing bands"): a host that omits every chip AND the next-action line
  // (the recomposed lead record) can still have NOTHING left to show
  // (overdueItems/historicalWarnings/the desk-closed note all empty too) --
  // chronicle-normal.png draws no dead card there. tests/e2e/role-
  // acceptance/lead-360-record.spec.ts pins this section as a permanently
  // VISIBLE structural landmark though (`record360-band` must stay in the
  // DOM with a non-zero box), so this shrinks the shell to a bare sliver
  // via CSS (--empty modifier) instead of unmounting it -- satisfies both
  // the pinned contract and the "no dead card" visual law.
  const isGenuinelyEmpty =
    overdueItems.length === 0 &&
    (['appointment', 'conversion', 'payment', 'identity', 'followup', 'sla', 'stage'] as const)
      .every((key) => !shows(key)) &&
    omitNextAction &&
    !truth.historicalWarnings.present &&
    truth.currentVisit.kind !== 'desk_closed_no_record';
  return (
    <section
      className={
        isGenuinelyEmpty ? 'record360-band record360-band--empty' : 'record360-band'
      }
      aria-label="Current state"
      data-testid="record360-band"
    >
      {overdueItems.length > 0 ? (
        <div className="record360-band__alerts" data-testid="record360-band-alerts">
          {overdueItems.map((u) => (
            <span
              key={u.key}
              className="record360-band__alert"
              data-testid={`record360-alert-${u.key}`}
            >
              Overdue - {u.label}
              {u.dueLabel !== null ? (
                <span className="record360-band__alert-due">
                  was due {u.dueLabel}
                </span>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      <div className="record360-band__chips">
        {shows('appointment') ? (
          <BandChip
            label="Appointment"
            tone={truth.currentAppointment.kind === 'none' ? 'neutral' : 'brand'}
            value={truth.currentAppointment.statusLabel ?? NO_APPOINTMENT_CHIP_LABEL}
            testId="record360-chip-appointment"
          />
        ) : null}
        {/* emphasis is EARNED by a real state -- an empty outcome renders
            the quiet default card, never the loudest element on the band
            (G2; values/tones/testids unchanged). */}
        {shows('conversion') ? (
          <BandChip
            label={CONVERSION_CHIP_LABEL}
            tone={CONVERSION_TONE[truth.currentConversionState.kind]}
            value={truth.currentConversionState.label}
            testId="record360-chip-conversion"
            emphasis={truth.currentConversionState.kind !== 'none'}
          />
        ) : null}
        {shows('payment') ? (
          <div
            className={
              truth.currentPaymentState.kind === 'none' ||
              truth.currentPaymentState.kind === 'unknown_on_this_read'
                ? 'record360-band__item'
                : 'record360-band__item record360-band__item--emphasis'
            }
            data-testid="record360-chip-payment"
          >
            <span className="record360-band__label">Payment</span>
            <AcBadge tone={PAYMENT_TONE[truth.currentPaymentState.kind]}>
              {truth.currentPaymentState.label}
            </AcBadge>
            {/* V1-P6 corrective (founder product law 2026-07-11): the derived
                payment state carries a ONE-CLICK route to its transaction
                evidence rows (the payments related list below) -- a derived
                state is not explainable unless its evidence is reachable
                in-product. Renders only when a payment plane exists. */}
            {truth.currentPaymentState.kind === 'collect_pending' ||
            truth.currentPaymentState.kind === 'recorded' ? (
              <a
                className="record360-band__evidence"
                href="#record360-zone-payments"
                data-testid="record360-payment-evidence-link"
              >
                View payment evidence
              </a>
            ) : null}
          </div>
        ) : null}
        {shows('identity') ? (
          <BandChip
            label="Patient record"
            tone={
              truth.identityState.kind === 'linked'
                ? 'positive'
                : truth.identityState.kind === 'possible_match'
                  ? 'brand'
                  : 'neutral'
            }
            value={truth.identityState.label}
            testId="record360-chip-identity"
          />
        ) : null}
        {shows('followup') ? (
          <BandChip
            label={FOLLOW_UP_CHIP_LABEL}
            tone={truth.currentFollowUpState.kind === 'required' ? 'info' : 'neutral'}
            value={truth.currentFollowUpState.whyLabel ?? FOLLOW_UP_NONE_LABEL}
            testId="record360-chip-followup"
          />
        ) : null}
        {/* MANAGER-OPS-COCKPIT-V2 (rider R-29): the SLA/waiting chip lights
            from the record-wire SLA seam through the contract's explicit
            states -- breach (worst severity), clear, or the honest
            "not available" (fail-safe omit); never a raw null. On a host that
            declares `omitChips` (the recomposed lead record), the SLA fact is
            carried by that host's own Waiting tile instead -- same contract
            value, same ONE classifier, rendered once. */}
        {shows('sla') ? (
          <BandChip
            label="SLA"
            tone={SLA_TONE[truth.currentSlaState.kind === 'breach' ? truth.currentSlaState.severity : 'neutral']}
            value={truth.currentSlaState.label}
            testId="record360-chip-sla"
          />
        ) : null}
        {shows('stage') ? (
          <BandChip
            label={STAGE_MICRO_LABEL}
            tone="neutral"
            value={truth.currentLeadStage.label}
            testId="record360-chip-stage"
          />
        ) : null}
      </div>
      {!omitNextAction ? (
        <p className="record360-band__next" data-testid="record360-next-action">
          <span className="record360-band__label">Next action</span>
          <strong data-source={truth.nextLawfulAction.source}>
            {truth.nextLawfulAction.label}
          </strong>
        </p>
      ) : null}
      {truth.historicalWarnings.present ? (
        // V7-P4A (rider R-32): the warnings strip is a LABELED list, not a
        // text wall -- a "History" micro-label + one compact list item per
        // warning. Content and order are the contract's labels verbatim;
        // this is layout only.
        <div
          className="record360-band__history"
          data-testid="record360-historical-warnings"
          role="note"
        >
          <span className="record360-band__history-title">History</span>
          <ul className="record360-band__history-list">
            {truth.historicalWarnings.labels.map((l) => (
              <li key={l} className="record360-band__history-line">
                {l}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {truth.currentVisit.kind === 'desk_closed_no_record' ? (
        // E-360-5 honest state, PROSE-DEMOTED (Fix 5 -- the working surface
        // is the labeled state; the why-this-is-valid explanation rides the
        // tooltip, not a rendered sentence).
        <p
          className="record360-band__note"
          data-testid="record360-visit-note"
          title="The front desk closed this visit and no doctor case sheet was recorded - a valid front-desk path, not an error."
        >
          {truth.currentVisit.label}
        </p>
      ) : null}
    </section>
  );
}
